import React, { useState, useCallback } from 'react';

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
import Cropper from 'react-easy-crop';
import toast from 'react-hot-toast';
import { X, ZoomIn, ZoomOut } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { uploadFile, handleUploadError, uploadErrorMessage } from '../../services/imageUploadService';
import { btn } from '../../utils/buttonStyles';

/**
 * ImageCropModal - Reusable image cropping + upload component
 *
 * Uploads to a temp UUID key and runs Rekognition moderation immediately at crop time.
 * ContentPolicyError is handled internally — triggers the global policy modal.
 * onCropComplete is only called when the image is approved, with { url, key }.
 * Callers are responsible for calling moveImage() to move to the canonical path
 * once they have the entity ID.
 *
 * @param {boolean}  isOpen         - Whether modal is visible
 * @param {function} onClose        - Close modal callback
 * @param {string}   imageSrc       - Source URL or data URL of image to crop
 * @param {string}   fileType       - S3 folder: profile_images | group_assets | team_assets | tournament_assets
 * @param {string}   [entityPath]   - Canonical entity path within the folder, e.g. '{userId}/profile.jpeg'.
 *                                    When provided (edit mode), the upload uses the canonical key so flagged
 *                                    images also land at the canonical flagged path.
 *                                    Omit for create mode (UUID temp key used instead).
 * @param {function} onCropComplete - Called with { url, key } on moderation approval
 * @param {number}   aspectRatio    - Aspect ratio (default: 1 for square/circle)
 * @param {string}   shape          - 'round' or 'rect' (default: 'round')
 */
export default function ImageCropModal({
  isOpen,
  onClose,
  imageSrc,
  fileType,
  entityPath,
  onCropComplete,
  aspectRatio = 1,
  shape = 'round'
}) {
  const { theme } = useTheme();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);   // cropping canvas
  const [saving, setSaving] = useState(false);     // uploading + moderation

  const onCropAreaChange = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (src, pixelCrop) => {
    const image = await createImage(src);

    // Cap output to 1600px on the longest side to keep JPEG blobs well under
    // Lambda's 6 MB buffered payload limit regardless of source image resolution.
    const MAX_SIDE = 1600;
    const scale = Math.min(1, MAX_SIDE / Math.max(pixelCrop.width, pixelCrop.height));
    const outW = Math.round(pixelCrop.width * scale);
    const outH = Math.round(pixelCrop.height * scale);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = outW;
    canvas.height = outH;
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, outW, outH);
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92));
  };

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setLoading(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      setLoading(false);

      if (!blob) {
        toast.error('Could not process this image. Please try a different photo.');
        return;
      }

      setSaving(true);
      try {
        const result = await uploadFile(blob, fileType, 'temp.jpeg', entityPath || undefined);
        onCropComplete?.(result);
        onClose();
      } catch (err) {
        if (!handleUploadError(err)) {
          toast.error(uploadErrorMessage(err));
        }
        onClose();
      } finally {
        setSaving(false);
      }
    } catch (err) {
      console.error('Error cropping image:', err);
      onClose();
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const busy = loading || saving;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/75" onClick={busy ? undefined : onClose} />

      <div className={`relative ${theme.card} rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text}`}>Adjust Your Photo</h3>
          <button
            onClick={onClose}
            disabled={busy}
            className={`p-2 ${theme.cardHover} rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <X className={`w-5 h-5 ${theme.textSecondary}`} />
          </button>
        </div>

        {/* Crop Area */}
        <div className="relative h-96 bg-black">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            cropShape={shape}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropAreaChange}
          />
        </div>

        {/* Controls */}
        <div className={`p-6 space-y-4 ${theme.backgroundSecondary}`}>
          <div className="space-y-2">
            <label className={`text-sm font-medium ${theme.text} flex items-center justify-between`}>
              <span className="flex items-center gap-x-2">
                <ZoomOut className="w-4 h-4" />
                <span>Zoom</span>
              </span>
              <ZoomIn className="w-4 h-4" />
            </label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>

          <p className={`text-sm ${theme.textSecondary}`}>
            Drag to reposition • Pinch or use slider to zoom
          </p>

          <div className="flex gap-x-3">
            <button
              onClick={onClose}
              disabled={busy}
              className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonGhost}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={busy}
              className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
            >
              {saving ? 'Checking image...' : loading ? 'Processing...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
